import type { SVGProps } from "react";

// jve-2-6: inline, currentColor-tinted line icons for the app shell (sidebar nav, location card,
// profile card). Mirrors the Jmix theme's sidebar glyph set (nav-*.svg / sidebar-proposal/*.svg) but
// as tintable React SVGs — no asset copying, crisp at any size, theme-aware via `color`.
// Extraction-ready: shared across backoffice + shelter-platform.

const base = (props: SVGProps<SVGSVGElement>): SVGProps<SVGSVGElement> => ({
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
  focusable: false,
  ...props,
});

export function MenuIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

export function ChevronLeftIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

/**
 * Lucide `paw-print` — the brand mark rendered white inside the sidebar's gradient logo tile.
 * Matches the live Jmix sidebar (frontend-react/sidebar) logo glyph.
 */
export function PawPrintIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <circle cx="11" cy="4" r="2" />
      <circle cx="18" cy="8" r="2" />
      <circle cx="20" cy="16" r="2" />
      <path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10Z" />
    </svg>
  );
}

/**
 * Lucide `panel-left-close` — the sidebar collapse control (a panel with a leftward chevron).
 * Replaces the plain chevron so the collapse affordance reads like the live Jmix sidebar.
 */
export function PanelLeftCloseIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M9 3v18" />
      <path d="m16 15-3-3 3-3" />
    </svg>
  );
}

/** Lucide `repeat` — the "Wisselen" (switch branch) affordance in the vestiging card header. */
export function RepeatIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="m17 2 4 4-4 4" />
      <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
      <path d="m7 22-4-4 4-4" />
      <path d="M21 13v1a4 4 0 0 1-4 4H3" />
    </svg>
  );
}

export function BuildingIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <rect x="4" y="3" width="11" height="18" rx="1" />
      <path d="M15 8h4a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-4" />
      <line x1="8" y1="7" x2="11" y2="7" />
      <line x1="8" y1="11" x2="11" y2="11" />
      <line x1="8" y1="15" x2="11" y2="15" />
    </svg>
  );
}

export function SwapIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <polyline points="16 3 20 7 16 11" />
      <line x1="20" y1="7" x2="4" y2="7" />
      <polyline points="8 21 4 17 8 13" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}

export function GearIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

export function LogoutIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

export function ShieldIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9.5 12.5l1.8 1.8 3.5-3.8" />
    </svg>
  );
}

export function UserIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <circle cx="12" cy="8" r="4" />
      <path d="M5.5 21a6.5 6.5 0 0 1 13 0" />
    </svg>
  );
}

export function BoxIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M21 8l-9-5-9 5v8l9 5 9-5V8z" />
      <polyline points="3 8 12 13 21 8" />
      <line x1="12" y1="13" x2="12" y2="21" />
    </svg>
  );
}

export function DropletIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M12 3s6 6.5 6 10.5a6 6 0 0 1-12 0C6 9.5 12 3 12 3z" />
      <path d="M9 14a3 3 0 0 0 3 3" />
    </svg>
  );
}

export function TagIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M3 11.5V4a1 1 0 0 1 1-1h7.5a1 1 0 0 1 .7.3l8 8a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0l-8-8a1 1 0 0 1-.3-.7z" />
      <circle cx="7.5" cy="7.5" r="1.5" />
    </svg>
  );
}

export function SearchIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

export function LockIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <rect x="4.5" y="11" width="15" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
      <circle cx="12" cy="15.5" r="1" />
    </svg>
  );
}

export function UnlockIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <rect x="4.5" y="11" width="15" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 7.5-1.9" />
      <circle cx="12" cy="15.5" r="1" />
    </svg>
  );
}

export function GlobeIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z" />
    </svg>
  );
}

export function EyeIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function EyeOffIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c6.5 0 10 7 10 7a17.8 17.8 0 0 1-3.06 4.06" />
      <path d="M6.06 6.06A17.8 17.8 0 0 0 2 11s3.5 7 10 7a9.1 9.1 0 0 0 4.06-.94" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <line x1="3" y1="3" x2="21" y2="21" />
    </svg>
  );
}

// jve-menu: the second wave of sidebar glyphs — line-icon equivalents of the Vaadin/Lumo icon names
// assigned in the Jmix `menu.xml` (DASHBOARD, ARCHIVE, HOME, CALENDAR_USER, …). Same currentColor /
// 24-grid / 1.8-stroke house style as the set above so the menu reads like Jmix without copying its
// PNG/Lumo assets. COG reuses the existing {@link GearIcon}.

/** Section toggle chevron (points down when the group is open; rotated by CSS when closed). */
export function ChevronDownIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/** Vaadin DASHBOARD — four tiles. */
export function DashboardIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

/** Vaadin ARCHIVE — lidded box. */
export function ArchiveIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
      <line x1="9.5" y1="12" x2="14.5" y2="12" />
    </svg>
  );
}

/** Vaadin HOME — house. */
export function HomeIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M3 11l9-7 9 7" />
      <path d="M5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9" />
      <path d="M9.5 20v-6h5v6" />
    </svg>
  );
}

/** Vaadin CALENDAR_USER — calendar with a person. */
export function CalendarUserIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <circle cx="12" cy="13.5" r="2" />
      <path d="M8.5 19a3.5 3.5 0 0 1 7 0" />
    </svg>
  );
}

/** Vaadin HANDSHAKE — two clasped hands. */
export function HandshakeIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M11 7L8 4 2 9v5l3 2" />
      <path d="M13 7l3-3 6 5v5l-3 2" />
      <path d="M8 9l3.5 3.5a1.5 1.5 0 0 0 2.1 0L17 9" />
      <path d="M5 16l3 3a1.5 1.5 0 0 0 2.1 0" />
    </svg>
  );
}

/** Vaadin AMBULANCE — van with a cross. */
export function AmbulanceIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M2 7h11v9H2z" />
      <path d="M13 10h4l3 3v3h-7z" />
      <circle cx="6.5" cy="18" r="1.8" />
      <circle cx="16.5" cy="18" r="1.8" />
      <line x1="7.5" y1="9.5" x2="7.5" y2="13.5" />
      <line x1="5.5" y1="11.5" x2="9.5" y2="11.5" />
    </svg>
  );
}

/** Vaadin FIRE — flame. */
export function FireIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M12 3c0 3-4 4-4 8a4 4 0 0 0 8 0c0-1.5-1-2.5-1.5-3.5C13 9 14 6 12 3z" />
      <path d="M11 21a3 3 0 0 0 2 0" />
    </svg>
  );
}

/** Vaadin TRUCK — box truck. */
export function TruckIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M2 6h11v10H2z" />
      <path d="M13 9h4l3 3v4h-7z" />
      <circle cx="6.5" cy="18" r="1.8" />
      <circle cx="16.5" cy="18" r="1.8" />
    </svg>
  );
}

/** Vaadin INBOX — tray. */
export function InboxIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M3 4h18v16H3z" />
      <path d="M3 14h5a2 2 0 0 0 4 0h0a2 2 0 0 0 4 0h5" />
    </svg>
  );
}

/** Vaadin FOLDER_OPEN — open folder. */
export function FolderOpenIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M3 7a1 1 0 0 1 1-1h5l2 2h8a1 1 0 0 1 1 1v2H3z" />
      <path d="M3 11h18l-2 8a1 1 0 0 1-1 .8H5a1 1 0 0 1-1-.8z" />
    </svg>
  );
}

/** Vaadin CALENDAR — month grid. */
export function CalendarIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="16" y1="2" x2="16" y2="6" />
    </svg>
  );
}

/** Vaadin PACKAGE — parcel. */
export function PackageIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M21 8l-9-5-9 5v8l9 5 9-5V8z" />
      <polyline points="3 8 12 13 21 8" />
      <line x1="12" y1="13" x2="12" y2="21" />
      <line x1="7.5" y1="5.5" x2="16.5" y2="10.5" />
    </svg>
  );
}

/** Vaadin INVOICE — document with lines. */
export function InvoiceIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M6 2h9l3 3v17l-2.5-1.5L13 22l-2.5-1.5L8 22l-2-1.5z" />
      <line x1="8.5" y1="8" x2="15.5" y2="8" />
      <line x1="8.5" y1="12" x2="15.5" y2="12" />
    </svg>
  );
}

/** Vaadin ENVELOPE — mail. */
export function EnvelopeIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <polyline points="3 7 12 13 21 7" />
    </svg>
  );
}

/** Vaadin RECORDS — stacked log lines. */
export function RecordsIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <line x1="7" y1="8.5" x2="17" y2="8.5" />
      <line x1="7" y1="12" x2="17" y2="12" />
      <line x1="7" y1="15.5" x2="13" y2="15.5" />
    </svg>
  );
}

/** Camera — the photo placeholder glyph (empty-state of an image preview frame). */
export function CameraIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M4 7h3l1.4-2h7.2L17 7h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Z" />
      <circle cx="12" cy="12.5" r="3.3" />
    </svg>
  );
}

// Animal "Basisinformatie" label glyphs — match the Jmix basis-info/*.svg label icons.

/** Venus / female — the Geslacht label. */
export function VenusIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <circle cx="12" cy="9" r="5" />
      <line x1="12" y1="14" x2="12" y2="22" />
      <line x1="9" y1="18.5" x2="15" y2="18.5" />
    </svg>
  );
}

/** Paw — the Diersoort / Ras / Uiterlijke-kenmerken label. */
export function PawIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <circle cx="6" cy="11" r="1.7" />
      <circle cx="10" cy="6.5" r="1.7" />
      <circle cx="14" cy="6.5" r="1.7" />
      <circle cx="18" cy="11" r="1.7" />
      <path d="M12 12.2c-2.4 0-4.6 1.9-4.6 4 0 1.6 1.3 2.6 3 2.3.6-.1 1-.3 1.6-.3s1 .2 1.6.3c1.7.3 3-.7 3-2.3 0-2.1-2.2-4-4.6-4z" />
    </svg>
  );
}

/** Comb — the Vacht (coat) label. */
export function CombIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M4 5.5h16v3H4z" />
      <line x1="7" y1="8.5" x2="7" y2="18.5" />
      <line x1="10.3" y1="8.5" x2="10.3" y2="18.5" />
      <line x1="13.7" y1="8.5" x2="13.7" y2="18.5" />
      <line x1="17" y1="8.5" x2="17" y2="18.5" />
    </svg>
  );
}

/** Palette — the Vacht kleur (coat colour) label. */
export function PaletteIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M12 3a9 9 0 1 0 0 18 1.8 1.8 0 0 0 1.8-1.8c0-.5-.2-.9-.5-1.2-.3-.3-.5-.7-.5-1.2a1.8 1.8 0 0 1 1.8-1.8H16a5 5 0 0 0 5-5c0-3.9-4-7-9-7z" />
      <circle cx="7.5" cy="11" r="1" />
      <circle cx="12" cy="7.5" r="1" />
      <circle cx="16.5" cy="11" r="1" />
    </svg>
  );
}

/** Microchip — the Chipnummer label. */
export function MicrochipIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <rect x="7" y="7" width="10" height="10" rx="1.5" />
      <line x1="10" y1="4" x2="10" y2="7" />
      <line x1="14" y1="4" x2="14" y2="7" />
      <line x1="10" y1="17" x2="10" y2="20" />
      <line x1="14" y1="17" x2="14" y2="20" />
      <line x1="4" y1="10" x2="7" y2="10" />
      <line x1="4" y1="14" x2="7" y2="14" />
      <line x1="17" y1="10" x2="20" y2="10" />
      <line x1="17" y1="14" x2="20" y2="14" />
    </svg>
  );
}

/** ID card — the Paspoort / Stamboomnummer label. */
export function IdCardIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="8.5" cy="11" r="2" />
      <path d="M5.5 16a3 3 0 0 1 6 0" />
      <line x1="14.5" y1="10" x2="18.5" y2="10" />
      <line x1="14.5" y1="13.5" x2="18.5" y2="13.5" />
    </svg>
  );
}

export function PencilIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

/** Trash — delete a note / media file. */
export function TrashIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

export function PinIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M9 4h6l-1 6 3 3v2H7v-2l3-3-1-6Z" />
      <line x1="12" y1="15" x2="12" y2="21" />
    </svg>
  );
}

/** Download — save a media file to disk. */
export function DownloadIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

/** Plus-in-circle — the Voeding "Toevoegen" action (mirrors the Jmix PLUS_CIRCLE button icon). */
export function PlusCircleIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="8" x2="12" y2="16" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}

/**
 * Hetzelfde plusrondje, maar dicht: een gevulde schijf met de plus eruit gespaard, zodat de kleur
 * van de knop erdoorheen komt. Dat is het rondje dat op de knoppen hoort — de lijnversie hierboven
 * verdwijnt op een gekleurde knop tot een paar losse streepjes.
 */
export function PlusCircleSolidIcon(p: SVGProps<SVGSVGElement>) {
  const { width = 18, height = 18, ...rest } = p;
  return (
    <svg
      viewBox="0 0 24 24"
      width={width}
      height={height}
      fill="currentColor"
      stroke="none"
      aria-hidden="true"
      {...rest}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1.1 5.4h-2.2v3.5H7.4v2.2h3.5v3.5h2.2v-3.5h3.5v-2.2h-3.5V7.4Z"
      />
    </svg>
  );
}

/** Plus — the "add file" card. */
export function PlusIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

/** Document/file — the placeholder glyph for a non-image media card. */
export function FileIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
    </svg>
  );
}

// Text/PDF document glyph (mirrors the Jmix FILE_TEXT icon for pdf/text uploads).
export function FileTextIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

/** Checkmark — the "file selected" tick in the media upload dialog's pending-files list. */
export function CheckIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/** Cross — removes a pending file from the media upload dialog's selection list. */
export function XIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/** Upload tray — the drag-and-drop zone glyph in the media upload dialog. */
export function UploadCloudIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
      <polyline points="16 16 12 12 8 16" />
      <line x1="12" y1="12" x2="12" y2="21" />
    </svg>
  );
}

// Photo-dialog adjust controls (Basisinformatie "Foto wijzigen") — mirror the Jmix VaadinIcon
// arrow / rotate / crosshair glyphs that nudge, rotate and centre the crop preview.

/** Arrow left — nudge the photo crop to the left. */
export function ArrowLeftIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

/** Arrow right — nudge the photo crop to the right. */
export function ArrowRightIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

/** Arrow up — nudge the photo crop upward. */
export function ArrowUpIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

/** Arrow down — nudge the photo crop downward. */
export function ArrowDownIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <polyline points="5 12 12 19 19 12" />
    </svg>
  );
}

/** Rotate left — turn the photo 90° counter-clockwise. */
export function RotateLeftIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <polyline points="3 5 3 11 9 11" />
      <path d="M3.5 11a8 8 0 1 1 1.6 5" />
    </svg>
  );
}

/** Rotate right — turn the photo 90° clockwise. */
export function RotateRightIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <polyline points="21 5 21 11 15 11" />
      <path d="M20.5 11a8 8 0 1 0-1.6 5" />
    </svg>
  );
}

/** Crosshairs — re-centre the photo crop (the "Centreren" reset). */
export function CrosshairsIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <circle cx="12" cy="12" r="7" />
      <line x1="12" y1="2" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="2" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="22" y2="12" />
    </svg>
  );
}

// Global-search (E3) glyphs — the header dropdown's "Slim zoeken" affordance, the recent-searches
// column and the "Alles" tab. Mirror the Jmix VaadinIcon.MAGIC / CLOCK / GRID_BIG glyphs.

/** Sparkles — the "Slim zoeken" / AI affordance (Jmix VaadinIcon.MAGIC). */
export function SparklesIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M11 4 12.4 8.6 17 10 12.4 11.4 11 16 9.6 11.4 5 10 9.6 8.6Z" />
      <path d="M18 4 18.6 6 20.5 6.6 18.6 7.2 18 9.2 17.4 7.2 15.5 6.6 17.4 6Z" />
      <path d="M17.5 15 18 16.7 19.7 17.2 18 17.7 17.5 19.4 17 17.7 15.3 17.2 17 16.7Z" />
    </svg>
  );
}

/** Clock — the "Recente zoekopdrachten" column header (Jmix VaadinIcon.CLOCK). */
export function ClockIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <circle cx="12" cy="12" r="8" />
      <polyline points="12 7 12 12 15 14" />
    </svg>
  );
}

/** Grid — the "Alles" type tab in the search dropdown (Jmix VaadinIcon.GRID_BIG). */
export function GridIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}

// Search-migration E4 (FR17–FR22) — additive glyphs for the full results page: the filter-row funnel,
// the backend-error empty state, the "Snelle acties" header, the "Tip" card, and the open/quick-action
// chevron. Mirror the Jmix VaadinIcon.FILTER / WARNING / BOLT / LIGHTBULB / CHEVRON_RIGHT.

/** Funnel — the filter-row icon + the all-types-off / facets-empty empty states (Jmix VaadinIcon.FILTER). */
export function FilterIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <polygon points="3 4 21 4 14 12.5 14 19 10 21 10 12.5" />
    </svg>
  );
}

/** Warning triangle — the backend-error empty state (Jmix VaadinIcon.WARNING). */
export function AlertTriangleIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M12 3.5 21.5 20H2.5L12 3.5Z" />
      <line x1="12" y1="9.5" x2="12" y2="14" />
      <line x1="12" y1="17" x2="12" y2="17" />
    </svg>
  );
}

/** Lightning bolt — the "Snelle acties" sidebar-card header (Jmix VaadinIcon.BOLT). */
export function BoltIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <polygon points="13 2 4 14 11 14 10 22 19 9 12 9" />
    </svg>
  );
}

/** Lightbulb — the "Tip" sidebar-card header (Jmix VaadinIcon.LIGHTBULB). */
export function LightbulbIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M9 18h6" />
      <path d="M10 21h4" />
      <path d="M12 3a6 6 0 0 1 4 10.5c-.7.6-1 1.2-1 2.5H9c0-1.3-.3-1.9-1-2.5A6 6 0 0 1 12 3Z" />
    </svg>
  );
}

/** Chevron right — the "Openen" link, recent-search rows and quick-actions (Jmix VaadinIcon.CHEVRON_RIGHT). */
export function ChevronRightIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

// ── Dier-aanwezig meta-row icons ────────────────────────────────────────────────────────────────
// Exact line-icon paths from the ground-truth `frontend-react/dier-aanwezig` Icon component, so the
// overview card's meta rows match acc glyph-for-glyph (kleur=heart, vacht=scissors, gewicht=scale,
// entingen=syringe, bijzonderheden=info, paspoort=book, opnamedatum=login).

/** Heart — the "Kleur" meta row (dier-aanwezig). */
export function HeartIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 1 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8z" />
    </svg>
  );
}

/** Scissors — the "Vacht" meta row. */
export function ScissorsIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M6 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM20 4 8.12 15.88M14.47 14.48 20 20M8.12 8.12 12 12" />
    </svg>
  );
}

/** Scale — the "Laatste gewicht" meta row. */
export function ScaleIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M12 3v18M7 7h10M5 7l-3 6a4 4 0 0 0 6 0zM19 7l-3 6a4 4 0 0 0 6 0zM7 21h10" />
    </svg>
  );
}

/** Syringe — the "Entingen ok" meta row. */
export function SyringeIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="m18 2 4 4M17 7l3-3M19 9 8.7 19.3a2.4 2.4 0 0 1-3.4 0l-.6-.6a2.4 2.4 0 0 1 0-3.4L15 5M9 11l4 4M5 19l-3 3M14 4l6 6" />
    </svg>
  );
}

/** Info — the "Bijzonderheden" meta row. */
export function InfoIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 16v-4M12 8h.01" />
    </svg>
  );
}

/** Book — the "Paspoortnummer" meta row. */
export function BookIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M4 19.5A2.5 2.5 0 0 0 6.5 22H20V2H6.5A2.5 2.5 0 0 0 4 4.5z" />
    </svg>
  );
}

/** Login (door-with-arrow) — the "Opnamedatum" meta row. */
export function LoginIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(p)}>
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" />
    </svg>
  );
}
